import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Play, Pause, Music } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type ShareStatus = "loading" | "active" | "expired" | "revoked" | "not_found" | "error";

interface ShareData {
  title: string | null;
  message: string | null;
  audioUrl: string | null;
  coverUrl: string | null;
  expiresAt: string | null;
}

const PublicPlayer = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<ShareStatus>("loading");
  const [data, setData] = useState<ShareData | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!token) { setStatus("not_found"); return; }

    const validate = async () => {
      try {
        const { data: resp, error } = await supabase.functions.invoke("validate-share", {
          body: { token },
        });

        if (error) { setStatus("error"); return; }

        if (resp.status === "active") {
          setData({
            title: resp.title,
            message: resp.message,
            audioUrl: resp.audioUrl,
            coverUrl: resp.coverUrl,
            expiresAt: resp.expiresAt,
          });
          setStatus("active");
        } else {
          setStatus(resp.status || "not_found");
          setData({ title: resp.title, message: null, audioUrl: null, coverUrl: null, expiresAt: null });
        }
      } catch {
        setStatus("error");
      }
    };

    validate();
  }, [token]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
    setDuration(audioRef.current.duration || 0);
  };

  const seekTo = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const Logo = () => (
    <img
      src="/images/YourMomentSonglogo_transparent_BG.png"
      alt="Your Moment Song"
      className="h-8 opacity-70"
    />
  );

  // ---------- States ----------
  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <Music className="w-10 h-10 text-primary" />
          <p className="text-muted-foreground text-sm">Loading your song…</p>
        </div>
      </div>
    );
  }

  if (status === "not_found" || status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-sm">
          <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-foreground mb-2">Song Not Found</h1>
          <p className="text-muted-foreground text-sm">
            This link doesn't match any song. Please check the URL or QR code and try again.
          </p>
          <div className="mt-8"><Logo /></div>
        </div>
      </div>
    );
  }

  if (status === "expired" || status === "revoked") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="text-center max-w-sm">
          <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-foreground mb-2">
            {status === "expired" ? "Link Expired" : "Link Unavailable"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {status === "expired"
              ? "This song link has expired. Contact the sender for a new link."
              : "This link is no longer active."}
          </p>
          {data?.title && (
            <p className="text-sm text-foreground mt-3 italic">"{data.title}"</p>
          )}
          <div className="mt-8"><Logo /></div>
        </div>
      </div>
    );
  }

  // ---------- Active Player ----------
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-card rounded-2xl shadow-lg overflow-hidden">
          {/* Cover */}
          {data?.coverUrl ? (
            <div className="aspect-square bg-muted">
              <img src={data.coverUrl} alt={data.title || "Cover"} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-square bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
              <Music className="w-20 h-20 text-primary/40" />
            </div>
          )}

          <div className="p-6 space-y-4">
            {/* Title */}
            <div className="text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Made just for you</p>
              <h1 className="text-xl font-semibold text-foreground font-serif">
                {data?.title || "Your Song"}
              </h1>
              {data?.message && (
                <p className="text-sm text-muted-foreground mt-2 italic">"{data.message}"</p>
              )}
            </div>

            {/* Player */}
            {data?.audioUrl && (
              <>
                <audio
                  ref={audioRef}
                  src={data.audioUrl}
                  preload="auto"
                  onTimeUpdate={onTimeUpdate}
                  onEnded={() => setPlaying(false)}
                  onLoadedMetadata={onTimeUpdate}
                />

                {/* Progress bar */}
                <div
                  className="w-full h-2 bg-muted rounded-full cursor-pointer relative"
                  onClick={seekTo}
                >
                  <div
                    className="h-full bg-primary rounded-full transition-[width] duration-200"
                    style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatTime(progress)}</span>
                  <span>{duration ? formatTime(duration) : "--:--"}</span>
                </div>

                {/* Play button */}
                <div className="flex justify-center">
                  <button
                    onClick={togglePlay}
                    className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md hover:opacity-90 transition-opacity"
                  >
                    {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Branding */}
        <div className="flex justify-center mt-6">
          <Logo />
        </div>
      </div>
    </div>
  );
};

export default PublicPlayer;
